import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import DataTable from '../../components/DataTable';
import StatusBadge from '../../components/StatusBadge';
import { useAuth } from '../../hooks/useAuth';
import { ROLES } from '../../utils/roles';
import * as paymentsApi from '../../api/payments.api';
import { errorMessage } from '../../api/client';
import { formatMoney, formatDateTime } from '../../utils/format';

function Payments() {
  const { user } = useAuth();
  const isAdmin = [ROLES.SUPER_ADMIN, ROLES.ADMIN].includes(user?.role);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const data = isAdmin ? await paymentsApi.listAdminPayments() : await paymentsApi.myPayments();
        if (!cancelled) setRows(data || []);
      } catch (e) {
        if (!cancelled) setErr(errorMessage(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [isAdmin]);

  return (
    <>
      <PageHeader
        title={isAdmin ? 'Payments' : 'Payment history'}
        subtitle={isAdmin ? 'All payments across the platform.' : 'Your payment receipts and statuses.'}
      />

      <DataTable
        loading={loading}
        rows={rows}
        searchPlaceholder="Search by gateway ref, order…"
        columns={[
          {
            key: 'gatewayRef',
            header: 'Gateway ref',
            render: (r) => <span className="cell-mono text-sm">{r.gatewayRef || '—'}</span>
          },
          {
            key: 'gateway',
            header: 'Gateway',
            render: (r) => <StatusBadge tone="brand">{r.gateway}</StatusBadge>
          },
          {
            key: 'order',
            header: 'Order',
            render: (r) => r.order?.orderNumber
              ? <Link to={`/orders/${r.order._id}`} className="cell-mono">{r.order.orderNumber}</Link>
              : '—'
          },
          ...(isAdmin ? [{
            key: 'user',
            header: 'User',
            render: (r) => <span>{r.user?.name || r.user?.email || '—'}</span>
          }] : []),
          {
            key: 'amount',
            header: 'Amount',
            render: (r) => <span className="cell-strong">{formatMoney(r.amount, r.currency || 'MYR')}</span>
          },
          {
            key: 'status',
            header: 'Status',
            render: (r) => <StatusBadge status={r.status} />
          },
          {
            key: 'paidAt',
            header: 'Paid at',
            render: (r) => <span className="muted">{r.paidAt ? formatDateTime(r.paidAt) : '—'}</span>
          },
          {
            key: 'createdAt',
            header: 'Created',
            render: (r) => <span className="muted">{formatDateTime(r.createdAt)}</span>
          }
        ]}
        emptyTitle="No payments yet"
      />
      {err && <div className="notice warning" style={{ marginTop: 'var(--s-4)' }}>{err}</div>}
    </>
  );
}

export default Payments;
